/**
 * Powerlete side-cart upsell.
 *
 * Server-side renders MPU + Meta cards; this component adds:
 *   - Add button state machine: is-picking → is-loading → is-success
 *     (mirrors live theme script-cdr.js _ctfNav / _ctfSetLoading / _ctfSetSuccess)
 *   - Size picker open/close with is-open + pw-sizes-in animation
 *   - Direct /cart/add.js fetch (bypasses product-form-component for state control)
 *   - cart:update dispatch → triggers Horizon's CartItemsComponent morph
 *   - Source blend (MPU + Meta + API recs), de-duped by product title, ordered per the
 *     merchant blend mode (shuffle once per visit / curated-first / fixed)
 *   - Carousel prev/next with overflow detection
 */

// Page-session caches (survive the element being re-rendered/morphed on every cart:update):
//  - RECS_CACHE dedupes identical /recommendations section fetches (same product+params).
//  - IMPRESSED memoises fired impression handle-sets so re-opening the drawer logs one, not N.
const PW_UPSELL_RECS_CACHE = new Map();
const PW_UPSELL_IMPRESSED = new Set();

// Cached fetch for a single recommendations URL. Shared cache (module-level) so
// re-fetching the same product+params during one page session is a no-op.
async function fetchRecsHtml(url) {
  let html = PW_UPSELL_RECS_CACHE.get(url);
  if (html != null) return html;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    html = await res.text();
  } catch {
    return null;
  }
  PW_UPSELL_RECS_CACHE.set(url, html);
  return html;
}

// Wait for an already-connected card's image to finish loading/decoding. Images
// in a DOMParser document never start fetching (that document is never "fully
// active"), so this must run AFTER the card is inserted into the live track —
// callers keep it visually hidden until this resolves. Bounded by a short
// timeout so one slow image can never stall the whole refill.
async function preloadCardImage(card, timeoutMs = 1200) {
  const img = card.querySelector('img');
  if (!img) return;
  const ready = img.decode
    ? img.decode().catch(() => {})
    : img.complete
      ? Promise.resolve()
      : new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        });
  await Promise.race([ready, new Promise((resolve) => setTimeout(resolve, timeoutMs))]);
}

if (!customElements.get('cart-upsell')) {
  customElements.define(
    'cart-upsell',
    class CartUpsell extends HTMLElement {
      #filling = false;
      #dialogObserver = null;
      #cartObserver = null;
      // Product id of whatever was last added via this rail, used to seed the next
      // refill's recommendations (see #fill) instead of re-querying the whole cart.
      #lastAddedProductId = null;
      // Has #fill() completed at least once? Gates the "stable refill" behaviour below —
      // the very first fill keeps the merchant's configured blend/shuffle untouched (nothing
      // is visible yet, so there's nothing to disrupt); every fill after that never lets a
      // currently-shown card be replaced in place — see #fill.
      #hasFilledOnce = false;
      // The API-recommendation cards (detached DOM nodes, already loaded/decoded) shown by
      // the MOST RECENT fill, in their displayed order. Needed because the cart:update morph
      // that precedes every refill resets the rail's live children back to the server-rendered
      // MPU/Meta cards ONLY — any previously JS-appended API cards are gone from the DOM by
      // the time #fill() runs again, even though they're still perfectly valid, already-loaded
      // nodes sitting in memory. Persisting them here (rather than re-deriving from the DOM,
      // which the morph has already wiped) is what lets a refill re-show a still-relevant
      // recommendation instantly, with zero network/image cost, instead of always treating it
      // as brand new.
      #lastRecsCards = [];
      // The full card sequence (curated + recs) as actually DISPLAYED by the most recent
      // fill — the reference order for every refill after it. Cards still wanted this fill
      // keep their position from here regardless of which bucket (curated vs recs) they
      // came from; anything not in this list is genuinely new and goes to the tail. This is
      // what lets a curated card land in the middle of the rail on the merchant's shuffled
      // first view and then STAY there on every refill, instead of snapping to the front
      // just because curated is concatenated first when building a fresh order.
      #lastOrderedCards = [];

      // Autoplay: pings-pongs the rail on a timer while it's visible and nothing is
      // holding it back. #autoplayPauseReasons is a SET, not a boolean — several
      // independent things can each demand a pause (touch, hover, keyboard focus, the
      // size picker being open, reduced motion, nothing to scroll, the rail being
      // mid-refill); autoplay only runs when the set is empty, so one reason clearing
      // can never accidentally resume it while another still applies.
      #autoplayEnabled = false;
      #autoplayIntervalMs = 5000;
      #autoplayDirection = 1; // 1 = forward, -1 = backward
      #autoplayTimer = null;
      #autoplayPauseReasons = new Set(['hidden']); // starts paused until proven visible
      #autoplayVisibilityObserver = null;
      #autoplayMotionQuery = null;
      #autoplayMotionListener = null;

      connectedCallback() {
        this.track = this.querySelector('[data-cart-upsell-track]');
        this.nav = this.querySelector('[data-cart-upsell-nav]');
        this.prevBtn = this.querySelector('[data-cart-upsell-prev]');
        this.nextBtn = this.querySelector('[data-cart-upsell-next]');

        this.#wireSizeToggles();
        this.#wireCarousel();
        this.#wireAutoplay();
        this.#setupFillTrigger();
        this.#writeMarketAttr();
      }

      disconnectedCallback() {
        this.#dialogObserver?.disconnect();
        this.#cartObserver?.disconnect();
        this.#autoplayVisibilityObserver?.disconnect();
        if (this.#autoplayMotionQuery && this.#autoplayMotionListener) {
          this.#autoplayMotionQuery.removeEventListener('change', this.#autoplayMotionListener);
        }
        if (this.#autoplayTimer) clearTimeout(this.#autoplayTimer);
        if (this.updateCarousel) window.removeEventListener('resize', this.updateCarousel);
      }

      // Defer the recs fill until the cart drawer is actually opened.
      // Re-fill on every cart morph (cart:update removes JS-appended API cards).
      #setupFillTrigger() {
        const dialog = this.closest('dialog');

        this.#cartObserver = new MutationObserver((mutations) => {
          if (this.#filling || (dialog && !dialog.open)) return;
          const morphed = mutations.some(
            (m) => m.type === 'attributes' || [...m.removedNodes].some((n) => n.nodeType === 1)
          );
          if (morphed) this.#fill();
        });
        this.#observeCart();

        if (!dialog) {
          this.#fill();
          return;
        }
        if (dialog.open) this.#fill();
        this.#dialogObserver = new MutationObserver(() => {
          if (dialog.open) this.#fill();
        });
        this.#dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
      }

      // (Re)attach the cart observer. Factored so #fill can detach around its own
      // DOM write and reattach with the identical config.
      #observeCart() {
        this.#cartObserver?.observe(this, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-in-cart'],
        });
      }

      // Wire all unwired add and toggle buttons. Idempotent: safe to re-run after
      // the fill appends API cards.
      #wireSizeToggles() {
        // Single-variant: direct add
        this.querySelectorAll('[data-cart-upsell-add]:not([data-pw-wired])').forEach((btn) => {
          btn.setAttribute('data-pw-wired', '');
          btn.addEventListener('click', () => {
            if (btn.disabled) return;
            const form = btn.closest('form');
            const variantId = form?.querySelector('input[name="id"]')?.value;
            if (!variantId) return;
            this.#atcFetch(btn, variantId, form);
          });
        });

        // Multi-variant: toggle opens size picker, size click adds
        this.querySelectorAll('[data-cart-upsell-toggle]:not([data-pw-wired])').forEach((toggle) => {
          const card = toggle.closest('[data-cart-upsell-card]');
          const sizes = card?.querySelector('[data-cart-upsell-sizes]');
          if (!sizes) return;
          toggle.setAttribute('data-pw-wired', '');
          const addLabel = toggle.dataset.addLabel || '+ ADD';

          toggle.addEventListener('click', () => {
            if (toggle.disabled) return;
            const isOpen = sizes.classList.contains('is-open');
            if (isOpen) {
              this.#closePicker(toggle, sizes, addLabel);
            } else {
              sizes.classList.add('is-open');
              toggle.setAttribute('aria-expanded', 'true');
              toggle.classList.add('is-picking');
              const openLabel = toggle.querySelector('.pw-upsell__add-label');
              if (openLabel) openLabel.textContent = '✕';
              // The ✕ glyph is not a meaningful accessible name; label the close action.
              toggle.setAttribute('aria-label', this.dataset.closeLabel || 'Close');
              const firstSize = sizes.querySelector('button:not([disabled])');
              if (firstSize) firstSize.focus();
              // Never let autoplay scroll the rail out from under a shopper who's
              // mid-pick — the card showing the size options must stay put.
              this.#pauseAutoplay('picker');
            }
          });

          sizes.querySelectorAll('[data-cart-upsell-size]').forEach((sizeBtn) => {
            sizeBtn.addEventListener('click', () => {
              if (sizeBtn.disabled) return;
              const variantId = sizeBtn.dataset.variantId;
              const form = toggle.closest('form');
              const idInput = form?.querySelector('input[name="id"]');
              if (!variantId || !idInput) return;

              this.#closePicker(toggle, sizes, addLabel);
              idInput.value = variantId;
              this.#atcFetch(toggle, variantId, form);
            });
          });
        });
      }

      #closePicker(toggle, sizes, addLabel) {
        sizes.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.classList.remove('is-picking');
        const closeLabel = toggle.querySelector('.pw-upsell__add-label');
        if (closeLabel) closeLabel.textContent = addLabel;
        // Back to the "+ Add" state: the button text is the accessible name again.
        toggle.removeAttribute('aria-label');
        this.#resumeAutoplay('picker');
      }

      // POST to /cart/add.js, show loading → success, dispatch cart:update
      async #atcFetch(btn, variantId, form) {
        if (!variantId || btn.disabled) return;
        this.#setLoading(btn, true);

        try {
          // Theme.routes.cart_add_url already ends in `.js` (scripts.liquid appends it); the
          // fallback carries `.js` too, and the fetch below no longer appends it (would double).
          const cartUrl = window.Theme?.routes?.cart_add_url ?? '/cart/add.js';
          const sectionIds = [...document.querySelectorAll('cart-items-component')]
            .map((el) => el.dataset.sectionId)
            .filter(Boolean);

          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', '1');
          // Carry line item properties (e.g. _upsell_source) from the surrounding form
          if (form) {
            for (const [key, val] of new FormData(form)) {
              if (key !== 'id' && key !== 'quantity') formData.append(key, val);
            }
          }
          if (sectionIds.length) formData.append('sections', sectionIds.join(','));

          const res = await fetch(cartUrl, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });

          if (!res.ok) {
            this.#setLoading(btn, false);
            return;
          }

          const cart = await res.json();

          // Capture card reference before the cart:update morph replaces the DOM
          const card = btn.closest('[data-cart-upsell-card]');
          const handle = card?.dataset.handle || '';
          const market = this.dataset.market || '';
          this.#lastAddedProductId = card?.dataset.productId || null;

          this.#setSuccess(btn);

          this.#fireUpsellEvent('cart_upsell_add', { product_handle: handle, variant_id: variantId, market });

          if (handle) {
            // sessionStorage can throw (private mode / quota / disabled storage). Never let that
            // turn a SUCCESSFUL add into an error state — the add already happened above.
            try {
              const existing = sessionStorage.getItem('_upsell_added') || '';
              const seen = existing ? existing.split(',') : [];
              if (!seen.includes(handle)) {
                seen.push(handle);
                const updated = seen.join(',');
                sessionStorage.setItem('_upsell_added', updated);
                this.#updateCartAttributes({ _upsell_triggered: 'true', _upsell_products_added: updated });
              }
            } catch (e) {
              /* storage unavailable: skip add-tracking, the add itself is unaffected */
            }
          }

          // Trigger Horizon's CartItemsComponent to morph + global analytics listener
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cart,
                sourceId: String(variantId),
                data: {
                  source: 'cart-upsell',
                  listName: 'Complete the Fit',
                  variantId: String(variantId),
                  productId: card?.dataset.productId || '',
                  sections: cart.sections || {},
                },
              },
            })
          );
        } catch {
          this.#setError(btn);
        }
      }

      // Fire Upstackified view_content for the card most visible in the track.
      // Called ~420ms after an arrow click so smooth scroll has settled.
      #fireUpstackViewContent() {
        if (typeof window._upstack !== 'function' || !this.track) return;
        const trackRect = this.track.getBoundingClientRect();
        let mostVisible = null;
        let maxOverlap = 0;
        this.track.querySelectorAll('[data-cart-upsell-card]').forEach((card) => {
          const rect = card.getBoundingClientRect();
          const overlap = Math.min(rect.right, trackRect.right) - Math.max(rect.left, trackRect.left);
          if (overlap > maxOverlap) { maxOverlap = overlap; mostVisible = card; }
        });
        if (!mostVisible) return;
        const productId = mostVisible.dataset.productId || '';
        const variantId = mostVisible.querySelector('input[name="id"]')?.value || '';
        const title = mostVisible.dataset.title || '';
        const price = (parseInt(mostVisible.dataset.price, 10) || 0) / 100;
        const currency = window.Shopify?.currency?.active || '';
        window._upstack('track', 'view_content', {
          value: price,
          currency,
          items: [{ id: productId, variant: variantId, name: title, sku: variantId, price, currency, subscription: false }],
        });
      }

      // Mirror live theme _updateCartAttributes — writes order-level attributes via cart
      async #updateCartAttributes(attrs) {
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: attrs }),
          });
        } catch (_) {}
      }

      // Mirror live theme _fireUpsellEvent — Shopify native analytics
      #fireUpsellEvent(eventName, data) {
        if (window.Shopify?.analytics?.publish) {
          window.Shopify.analytics.publish(eventName, data);
        }
      }

      // Mirror live theme _ctfSetLoading
      #setLoading(btn, flag) {
        btn.classList.toggle('is-loading', flag);
        btn.disabled = flag;
      }

      #setError(btn) {
        this.#setLoading(btn, false);
        const card = btn.closest('[data-cart-upsell-card]');
        if (!card) return;
        let msg = card.querySelector('.pw-upsell__atc-error');
        if (!msg) {
          msg = document.createElement('p');
          msg.className = 'pw-upsell__atc-error';
          btn.insertAdjacentElement('afterend', msg);
        }
        msg.textContent = this.dataset.errorText || 'Something went wrong. Please try again.';
        clearTimeout(msg._timer);
        msg._timer = setTimeout(() => msg.remove(), 4000);
      }

      // Mirror live theme _ctfSetSuccess (1800ms green flash)
      #setSuccess(btn) {
        btn.classList.remove('is-loading');
        btn.classList.add('is-success');
        btn.disabled = true;
        setTimeout(() => {
          btn.classList.remove('is-success');
          btn.disabled = false;
        }, 1800);
      }

      // Carousel prev/next arrows, shown only when track overflows
      #wireCarousel() {
        if (!this.track || !this.nav) return;

        this.updateCarousel = () => {
          const overflow = this.track.scrollWidth - this.track.clientWidth > 2;
          this.nav.hidden = !overflow;
          if (!overflow) {
            // Nothing to scroll: clear both edge fades so a short rail isn't masked, and
            // hold autoplay off — a single-card rail has nowhere to ping-pong to.
            this.track.classList.remove('pw-upsell__track--fade-start', 'pw-upsell__track--fade-end');
            this.#pauseAutoplay('no-overflow');
            return;
          }
          this.#resumeAutoplay('no-overflow');
          const max = this.track.scrollWidth - this.track.clientWidth;
          const left = this.track.scrollLeft;
          if (this.prevBtn) this.prevBtn.disabled = left <= 2;
          if (this.nextBtn) this.nextBtn.disabled = left >= max - 2;
          // Fade only the side that still has cards beyond the edge (never the last card at rest).
          this.track.classList.toggle('pw-upsell__track--fade-start', left > 2);
          this.track.classList.toggle('pw-upsell__track--fade-end', left < max - 2);
        };

        this.prevBtn?.addEventListener('click', () => this.#page(-1));
        this.nextBtn?.addEventListener('click', () => this.#page(1));
        this.track.addEventListener('scroll', this.updateCarousel, { passive: true });
        window.addEventListener('resize', this.updateCarousel, { passive: true });
        this.updateCarousel();
      }

      // Each card's start position in the rail's own scroll coordinates, sub-pixel accurate.
      // getBoundingClientRect (not offsetLeft/offsetWidth) because the card width is a
      // PERCENTAGE of the rail and therefore fractional, while the offset* properties round
      // to whole pixels.
      #cardEdges() {
        const cards = this.track.querySelectorAll('[data-cart-upsell-card]');
        if (!cards.length) return [];
        // scrollLeft is measured from the padding box, so clientLeft (the border) is the
        // origin, not the border-box edge getBoundingClientRect reports.
        const origin = this.track.getBoundingClientRect().left + this.track.clientLeft;
        const scrolled = this.track.scrollLeft;
        return Array.from(cards, (card) => card.getBoundingClientRect().left - origin + scrolled);
      }

      // Scroll the rail to the next card's real edge, forward (1) or backward (-1). Shared by
      // the manual prev/next buttons and autoplay's ping-pong ticks (see #autoplayTick), so
      // both land on identical positions.
      //
      // Deliberately scrollTo(a measured edge) rather than scrollBy(a computed step): a step of
      // card-width + gap is always a fraction out (see #cardEdges), and because scrollBy is
      // relative that error compounds tick over tick. Only scroll-snap was hiding it, and snap
      // is off on desktop by design — so the step has to be absolute, not arithmetic.
      #page(dir) {
        if (!this.track) return;
        const edges = this.#cardEdges();
        if (!edges.length) return;
        // Resolve where we are from the rail itself rather than tracking an index, so a
        // shopper's own swipe (or a refill reordering the cards) can't desync us.
        const current = this.track.scrollLeft;
        let index = 0;
        let closest = Infinity;
        edges.forEach((edge, i) => {
          const distance = Math.abs(edge - current);
          if (distance < closest) {
            closest = distance;
            index = i;
          }
        });
        const target = edges[Math.min(Math.max(index + dir, 0), edges.length - 1)];
        const max = this.track.scrollWidth - this.track.clientWidth;
        this.track.scrollTo({
          left: Math.min(Math.max(target, 0), max),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
        // Fire view_content for the card that lands in view after smooth scroll settles
        setTimeout(() => this.#fireUpstackViewContent(), 420);
      }

      // Autoplay: ping-pongs the rail on a timer. See the #autoplay* field comments for
      // the pause-reason model. Wired once from connectedCallback; #wireCarousel's
      // updateCarousel() supplies the 'no-overflow' reason, #wireSizeToggles/#closePicker
      // supply 'picker', and #fill's try/finally supply 'filling' (all via #pauseAutoplay /
      // #resumeAutoplay below).
      #wireAutoplay() {
        if (!this.track || !this.nav) return;

        this.#autoplayEnabled = this.dataset.autoplayEnabled !== 'false';
        const intervalSec = parseFloat(this.dataset.autoplayInterval);
        this.#autoplayIntervalMs = Math.max(1000, (Number.isFinite(intervalSec) ? intervalSec : 5) * 1000);
        if (!this.#autoplayEnabled) return;

        // Only run while genuinely visible on screen. This alone correctly covers the
        // cart DRAWER (a closed native <dialog> has no layout box while closed, so
        // intersection is false until showModal() opens it — no separate dialog-open
        // detection needed) and the standalone /cart PAGE context, where the rail should
        // only animate once it's actually scrolled into view.
        this.#autoplayVisibilityObserver = new IntersectionObserver(
          (entries) => {
            const visible = entries[entries.length - 1]?.isIntersecting;
            if (visible) this.#resumeAutoplay('hidden');
            else this.#pauseAutoplay('hidden');
          },
          { threshold: 0.1 }
        );
        this.#autoplayVisibilityObserver.observe(this);

        // Reduced motion: never autoplay at all, and keep watching in case the shopper's
        // OS-level setting changes while the drawer is open.
        this.#autoplayMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.#autoplayMotionListener = () => {
          if (this.#autoplayMotionQuery.matches) this.#pauseAutoplay('motion');
          else this.#resumeAutoplay('motion');
        };
        this.#autoplayMotionListener();
        this.#autoplayMotionQuery.addEventListener('change', this.#autoplayMotionListener);

        // Pause the instant a shopper touches/drags the rail; resume (with a fresh full
        // interval, not an immediate jump) once they let go.
        this.track.addEventListener('pointerdown', () => this.#pauseAutoplay('interaction'), { passive: true });
        const endTouch = () => this.#resumeAutoplay('interaction');
        this.track.addEventListener('pointerup', endTouch, { passive: true });
        this.track.addEventListener('pointercancel', endTouch, { passive: true });

        // Desktop hover and keyboard focus are the standard carousel "I'm engaging with
        // this" signals — never let autoplay fight a shopper who's looking at or tabbing
        // through the cards.
        this.addEventListener('mouseenter', () => this.#pauseAutoplay('hover'));
        this.addEventListener('mouseleave', () => this.#resumeAutoplay('hover'));
        this.addEventListener('focusin', () => this.#pauseAutoplay('focus'));
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.#resumeAutoplay('focus');
        });
      }

      // Add a hold on autoplay. Multiple independent callers can each hold their own
      // reason at once; autoplay stays off until every reason has been released.
      #pauseAutoplay(reason) {
        this.#autoplayPauseReasons.add(reason);
        if (this.#autoplayTimer) {
          clearTimeout(this.#autoplayTimer);
          this.#autoplayTimer = null;
        }
      }

      // Release a hold. If nothing else is holding autoplay back, this starts a FRESH
      // full-length countdown to the next tick — never an immediate scroll — so autoplay
      // never feels like it's fighting whatever the shopper just finished doing.
      #resumeAutoplay(reason) {
        this.#autoplayPauseReasons.delete(reason);
        this.#scheduleAutoplayTick();
      }

      #scheduleAutoplayTick() {
        if (this.#autoplayTimer || this.#autoplayPauseReasons.size > 0 || !this.#autoplayEnabled) return;
        this.#autoplayTimer = setTimeout(() => this.#autoplayTick(), this.#autoplayIntervalMs);
      }

      // Ping-pong: step in the current direction, flipping it first if the rail is
      // already sitting at (or past) the edge it would otherwise scroll beyond.
      #autoplayTick() {
        this.#autoplayTimer = null;
        if (this.#autoplayPauseReasons.size > 0 || !this.track) return;
        const max = this.track.scrollWidth - this.track.clientWidth;
        const left = this.track.scrollLeft;
        if (left >= max - 2) this.#autoplayDirection = -1;
        else if (left <= 2) this.#autoplayDirection = 1;
        this.#page(this.#autoplayDirection);
        this.#scheduleAutoplayTick();
      }

      // Blend + fill the rail from three sources: MPU (per-market localization), Meta
      // (curated "complete the fit" coordinates from custom.upsell_products) and API
      // recommendations. De-duped by product TITLE so multiple colourways of one style
      // collapse to a single card (priority meta > mpu > api = curated coordinates win),
      // while colour-coordinated DIFFERENT styles are all kept. The blend is then shuffled
      // once per visit (session-seeded) so it feels varied but stays stable while browsing.
      async #fill() {
        if (this.#filling) return;
        this.track = this.querySelector('[data-cart-upsell-track]');
        if (!this.track) return;

        const max = parseInt(this.dataset.max, 10) || 0;
        if (!max) return;
        const recsUrl = this.dataset.recsUrl;
        const recsSection = this.dataset.recsSection;
        const intent = this.dataset.intent || 'related';
        const sectionId = this.dataset.sectionId;
        const recsEnabled = this.dataset.recsEnabled !== 'false';
        const dedupeColourways = this.dataset.dedupeColourways !== 'false';
        const blendMode = this.dataset.blendMode || 'shuffle';
        // 1 = cart items only (previous behaviour), 2 = plus one hop from what those recommended.
        const recsDepth = parseInt(this.dataset.recsDepth, 10) === 1 ? 1 : 2;

        // Collect server-rendered cards by source.
        const mpu = [];
        const meta = [];
        const seenIds = new Set();
        this.track.querySelectorAll('[data-cart-upsell-card]').forEach((c) => {
          if (c.dataset.productId) seenIds.add(c.dataset.productId);
          if (c.dataset.upsellSource === 'mpu') mpu.push(c);
          else meta.push(c);
        });

        // The very first fill has nothing on-screen yet, so the merchant's configured
        // blend/shuffle applies exactly as authored. Every fill after that is a "refill"
        // (triggered by cart:update) — see the ordered-array + #lastRecsCards logic below
        // for what changes.
        const isRefill = this.#hasFilledOnce;
        this.#hasFilledOnce = true;

        this.#filling = true;
        this.#pauseAutoplay('filling');
        try {
          // De-dupe by product TITLE (colourways of one style share an identical title) so
          // curated coordinates win and the rail fills to `max` unique styles. The colourway
          // de-dupe is merchant-toggleable; product-id de-dupe (seenIds) always runs. Cards are
          // bucketed so the "curated first" blend mode can order them ahead of recommendations.
          const titleKey = (c) =>
            (c.dataset.title || c.querySelector('.pw-upsell__name')?.textContent || '').trim().toLowerCase();
          // Seeded with the CART's product titles, so a colourway of something already in the
          // basket is never offered back. The `seenIds` / `data-in-cart` pass only removes the
          // exact product, which left the rail spending a slot on e.g. Burgundy leggings when the
          // shopper had just chosen the Black ones. Seeded here, before any card is added, so it
          // applies to every source (mpu, meta and API) rather than recommendations alone.
          // Independent of `recsDepth` — this is a correctness fix, not a preference. It does still
          // follow `dedupeColourways` (the check lives in addCard): a merchant who has switched
          // colourway de-duping off wants colourways shown, and cart siblings are colourways.
          const seenTitles = new Set(
            (this.dataset.inCartTitles || '')
              .split('|')
              .map((t) => t.trim().toLowerCase())
              .filter(Boolean)
          );
          const curated = []; // market push + curated coordinates (meta + mpu)
          const recs = []; // API recommendations
          const total = () => curated.length + recs.length;
          const addCard = (card, bucket, cap = max) => {
            if (total() >= cap) return false;
            const key = titleKey(card) || card.dataset.productId;
            if (dedupeColourways && key && seenTitles.has(key)) return false;
            if (key) seenTitles.add(key);
            bucket.push(card);
            return true;
          };
          // Coordinates (meta) before market push (mpu) so they win title collisions.
          meta.forEach((c) => addCard(c, curated));
          mpu.forEach((c) => addCard(c, curated));

          // Refill only: re-show whatever API recommendations were already displayed, in
          // their existing order, before fetching anything new. The cart:update morph that
          // precedes this call already wiped them from the live DOM (server HTML never
          // includes API cards — see #lastRecsCards), but the nodes themselves are still
          // valid and fully loaded, so re-inserting them costs nothing and the shopper never
          // sees them disappear.
          //
          // Reserve one slot (cap at max-1, not max) ONLY when this refill was triggered by
          // an actual add via this rail (#lastAddedProductId set) — that's a concrete signal
          // harvestRecs below is likely to find something newly relevant. A refill with no
          // such signal (a quantity change or removal elsewhere in the cart) keeps every kept
          // card: evicting one on a guess, with nothing to replace it, would shrink the rail
          // for no reason. Whatever the cap does drop is whichever kept card is LAST in the
          // existing order — already furthest along the rail, least likely to be in view.
          if (isRefill && this.#lastRecsCards.length) {
            const inCartIds = new Set(
              (this.dataset.inCart || '').split(',').map((s) => s.trim()).filter(Boolean)
            );
            const reserveForFresh = recsEnabled && recsUrl && recsSection && this.#lastAddedProductId ? 1 : 0;
            const keptCap = Math.max(0, max - reserveForFresh);
            for (const card of this.#lastRecsCards) {
              const pid = card.dataset.productId;
              // Dropped from `recs`, not the DOM: a card for a product the shopper just
              // added is still their own element until the *next* fill's morph clears it.
              if (!pid || inCartIds.has(pid)) continue;
              if (addCard(card, recs, keptCap)) seenIds.add(pid);
            }
          }

          // Fetch recommendations for `productIds` concurrently (bounded by the slowest
          // single request, not their sum) and merge unique cards into `recs`.
          const recsFor = (productId, forIntent) => {
            // Always ask for Shopify's maximum (10), rather than sizing the request to the number
            // of empty slots. Fetch depth and DISPLAY depth are different quantities on this store:
            // one product per colourway means 10 recommendations routinely collapse to 2-3 unique
            // styles after the title de-dupe. The old `Math.min(10, max - total() + 3)` window
            // measured 6 for a half-full rail, and those 6 rows collapsed to 2 titles that were
            // both already on screen -> 0 usable, rail stuck. Same request count either way; only
            // the rows-per-response changes, and the response is already filtered server-side.
            const limit = 10;
            const url = `${recsUrl}?section_id=${encodeURIComponent(recsSection)}&product_id=${encodeURIComponent(productId)}&intent=${encodeURIComponent(forIntent)}&limit=${limit}`;
            return fetchRecsHtml(url);
          };

          // `intent=complementary` returns ZERO products unless complementary products are set up
          // in Shopify's Search & Discovery app. This store has not set them up, so selecting that
          // option in the theme editor silently emptied the entire API source and dropped the rail
          // to mpu + curated only. The setting looks like a normal choice, so nothing surfaced it.
          // Retry once with `related` when the configured intent yields nothing. No-op when the
          // configured intent IS `related` — no retry, no second request.
          // Test for CARDS, not for a non-empty response. The recommendations endpoint returns a
          // rendered SECTION, and that section renders its wrapper markup perfectly happily with
          // zero products in it — so `html.trim()` is truthy even when the intent matched nothing,
          // and a length check would never fire the fallback. Verified: complementary returned a
          // ~non-empty body containing no cards at all.
          const hasCards = (html) => !!html && html.includes('data-cart-upsell-card');
          const recsWithFallback = async (productId) => {
            const html = await recsFor(productId, intent);
            if (intent === 'related' || hasCards(html)) return html;
            return recsFor(productId, 'related');
          };

          // Returns { added, candidates }: the ids it put on the rail, and every id it parsed
          // (including ones dropped as duplicates). The second hop seeds from `added` first, but
          // needs `candidates` as a fallback — see the depth-2 pass below for why.
          const harvestRecs = async (productIds) => {
            const added = [];
            const candidates = [];
            const htmls = await Promise.all(productIds.map((productId) => recsWithFallback(productId)));
            for (const html of htmls) {
              if (total() >= max) break;
              if (!html?.trim()) continue;
              const doc = new DOMParser().parseFromString(html, 'text/html');
              for (const card of doc.querySelectorAll('[data-cart-upsell-card]')) {
                if (total() >= max) break;
                const pid = card.dataset.productId;
                if (!pid) continue;
                candidates.push(pid);
                if (seenIds.has(pid)) continue;
                if (sectionId) {
                  card.querySelectorAll('product-form-component').forEach((el) =>
                    el.setAttribute('data-section-id', sectionId)
                  );
                }
                if (addCard(card, recs)) {
                  seenIds.add(pid);
                  added.push(pid);
                }
              }
            }
            return { added, candidates };
          };

          // Top up with API recommendations (if enabled) until the rail hits max styles.
          if (recsEnabled && recsUrl && recsSection && total() < max) {
            const cartIds = (this.dataset.inCart || '')
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
            cartIds.forEach((id) => seenIds.add(id));

            // Seed from whichever product triggered this refill (the item just added), so
            // the rail updates to complement THAT pick rather than re-querying every
            // product in the cart on every single add. Falls back to the whole cart when
            // there's no "just added" product yet (e.g. the first fill on drawer open).
            const primary =
              this.#lastAddedProductId && cartIds.includes(this.#lastAddedProductId)
                ? [this.#lastAddedProductId]
                : cartIds;
            const hop1 = await harvestRecs(primary);
            let hop2Added = hop1.added;
            let hop2Fallback = hop1.candidates;

            // Primary seed alone didn't fill the rail (e.g. few related products) — top
            // up from the rest of the cart, still fired concurrently, not sequentially.
            if (total() < max) {
              const remaining = cartIds.filter((id) => !primary.includes(id));
              if (remaining.length) {
                const more = await harvestRecs(remaining);
                hop2Added = hop2Added.concat(more.added);
                hop2Fallback = hop2Fallback.concat(more.candidates);
              }
            }

            // Second hop. Shopify's `related` recommendations are colourway-heavy on this store
            // (one product per colourway), so after the title de-dupe a single cart item yields
            // only 2-5 unique STYLES — and `related` is capped at 10 per seed, so asking for more
            // buys nothing. There is no deeper well at one hop. Asking what goes with the products
            // hop 1 just picked opens a fresh one: measured 2026-07-18 on the live US storefront,
            // a leggings cart went 4 -> 6 cards, a bra cart 3 -> 7, a shorts cart 6 -> 13.
            //
            // Bounded deliberately, because relevance drifts one step further from the cart with
            // every hop:
            //   - ONE extra hop. There is no hop 3.
            //   - seeded from what hop 1 ADDED, preferentially;
            //   - capped at the first 3 seeds, so a large cart cannot fan out;
            //   - skipped entirely when the rail already reached max.
            // Cost is at most 3 requests (6 in the misconfigured-complementary case, where each
            // seed retries once via recsWithFallback), fired concurrently, served from RECS_CACHE
            // on repeat seeds, and only ever after the drawer is already open — never on the load
            // path.
            //
            // Seeding from `added` ALONE starves the rail in exactly the case that needs help
            // most: when everything hop 1 returned was already on screen it adds nothing, so
            // there is no seed, so no second hop, so the rail stays short. Verified: a leggings
            // cart with 3 curated cards had hop 1 return only titles already shown -> 0 added ->
            // rail stuck at 3 of 6. So fall back to the ids hop 1 merely SAW. Those are still
            // Shopify's own recommendations for a cart product, one hop from the basket exactly
            // like the added ones — only their titles were duplicates, which says nothing about
            // their usefulness as a seed.
            if (recsDepth === 2 && total() < max) {
              // Fallback ids can include a product already used as a hop-1 seed (cart item A's
              // recommendations can contain cart item B). Harmless: RECS_CACHE serves the repeat
              // without a network round trip.
              const hop2Seeds = (hop2Added.length ? hop2Added : hop2Fallback).slice(0, 3);
              if (hop2Seeds.length) await harvestRecs(hop2Seeds);
            }
          }

          // Combine per the merchant's blend mode — but only on the very first fill. Every
          // refill after that always concatenates curated+recs with NO shuffle, regardless
          // of the merchant's setting: shuffling here would scramble the kept-then-new order
          // built above, undoing it — a currently-visible card could get shuffled into a
          // different slot, or a slot the shopper is looking at could get shuffled INTO by a
          // card that isn't preloaded, both exactly the disruption this exists to prevent.
          // Variety from the configured blend mode still happens once, on first open; after
          // that, new recommendations only ever arrive by being appended past what's kept,
          // never by displacing something already on screen. On a refill, position comes
          // from #lastOrderedCards (what was actually shown last time) rather than bucket
          // type, so e.g. a curated card the initial shuffle happened to place mid-rail
          // STAYS there instead of snapping to the front just because curated is normally
          // concatenated first.
          let ordered;
          if (isRefill && this.#lastOrderedCards.length) {
            const wanted = [...curated, ...recs];
            const wantedSet = new Set(wanted);
            const stillThere = this.#lastOrderedCards.filter((card) => wantedSet.has(card));
            const stillThereSet = new Set(stillThere);
            const brandNew = wanted.filter((card) => !stillThereSet.has(card));
            ordered = [...stillThere, ...brandNew];
          } else if (blendMode === 'fixed') {
            ordered = [...curated, ...recs];
          } else if (blendMode === 'curated_first') {
            ordered = [...this.#shuffleForVisit(curated), ...this.#shuffleForVisit(recs)];
          } else {
            ordered = this.#shuffleForVisit([...curated, ...recs]);
          }

          // Remember this fill's full display order AND its API recs specifically — both
          // derived from `ordered` (what was actually shown), not from `curated`/`recs`
          // directly, since either the merchant's blend mode (first fill) or the isRefill
          // branch just above (every fill after) may have reordered them from build order.
          const recsSet = new Set(recs);
          this.#lastRecsCards = ordered.filter((card) => recsSet.has(card));
          this.#lastOrderedCards = ordered;

          // Cards not yet in the live rail (freshly parsed from a detached DOMParser
          // document, which never actually fetches images — that document is never
          // "fully active") must be connected before their image can load. Checked via
          // ownerDocument, NOT isConnected: a node parsed by DOMParser is still "connected"
          // by the DOM spec's definition (its root is *a* document, just not this page's),
          // so isConnected is true for it from the moment it's parsed and never flips.
          // Force eager loading and hide these cards so connecting doesn't flash a blank
          // frame in; they're revealed only once actually loaded, below.
          const newCards = ordered.filter((card) => card.ownerDocument !== document);
          newCards.forEach((card) => {
            const img = card.querySelector('img');
            if (img) img.loading = 'eager';
            card.style.visibility = 'hidden';
          });

          // Reconcile the rail to `ordered` in place instead of wiping and rebuilding it:
          // cards already showing the right product are left completely untouched (their
          // <img> never reloads), only genuinely new/moved cards touch the DOM (connected
          // hidden, per above). Detach the cart observer around our own controlled write
          // (reattached in finally) — the observer watches this subtree's childList, and
          // inserting/removing children here would otherwise queue mutation records that
          // re-invoke #fill once the #filling guard is cleared, an infinite microtask loop
          // that pegs the main thread.
          this.#cartObserver?.disconnect();
          let cursor = this.track.firstChild;
          for (const card of ordered) {
            if (cursor === card) {
              cursor = cursor.nextSibling;
            } else {
              this.track.insertBefore(card, cursor);
            }
          }
          for (const child of [...this.track.children]) {
            if (!ordered.includes(child)) child.remove();
          }
          this.#wireSizeToggles();

          // New cards are now connected and actually fetching — wait for each to finish
          // loading/decoding, then reveal it. Existing cards were never hidden, so they're
          // untouched throughout.
          await Promise.all(
            newCards.map((card) => preloadCardImage(card).then(() => { card.style.visibility = ''; }))
          );
        } catch {
          /* network/parse failure: leave server-rendered rail as-is */
        } finally {
          // Reattach in finally so the observer is restored even if the re-render
          // throws mid-write (a detached observer would silently stop the rail from
          // ever re-filling). Synchronous here, so it lands before any microtask.
          this.#observeCart();
          this.#filling = false;
          this.#resumeAutoplay('filling');
          this.#trackImpression();
          this.updateCarousel?.();
          // Consumed by this fill (both the reserve-a-slot decision above and the recs
          // seed below) — clear it so the NEXT refill, if nothing new gets added before
          // it, has no stale "just added" signal. Without this, a later refill caused by
          // something unrelated (removing a different line item, a quantity change) would
          // keep reserving/evicting a kept card on the belief something is still freshly
          // relevant, when the actual add that set this happened fills ago.
          this.#lastAddedProductId = null;
        }
      }

      // Fisher-Yates shuffle seeded by a per-visit value (see #visitSeed) so the order is
      // stable for the whole session but differs between visits/shoppers.
      #shuffleForVisit(arr) {
        const a = arr.slice();
        let seed = this.#visitSeed();
        const rand = () => {
          seed = (seed + 0x6d2b79f5) | 0;
          let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
          t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
          return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
        };
        for (let i = a.length - 1; i > 0; i--) {
          const j = Math.floor(rand() * (i + 1));
          [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
      }

      // One seed per visit, persisted in sessionStorage. Falls back to a fixed seed when
      // storage is unavailable (private mode) — order stays deterministic, just not random.
      #visitSeed() {
        try {
          let s = sessionStorage.getItem('pw_upsell_seed');
          if (!s) {
            s = String((Math.floor(Math.random() * 0xffffffff) >>> 0) || 1);
            sessionStorage.setItem('pw_upsell_seed', s);
          }
          return parseInt(s, 10) >>> 0;
        } catch {
          return 0x9e3779b1;
        }
      }

      // Mirror live theme page-load block (script-cdr.js lines 2106-2109):
      // write _upsell_market once per session so checkout-level analytics can
      // attribute which market the upsell was shown in, even without cart open.
      #writeMarketAttr() {
        // Guard the once-per-session bookkeeping; if storage throws, degrade to writing the attr
        // once per page load rather than failing.
        try {
          if (sessionStorage.getItem('_upsell_market_set')) return;
          sessionStorage.setItem('_upsell_market_set', '1');
        } catch (e) {
          /* storage blocked: fall through and write anyway */
        }
        const market = this.dataset.market || '';
        if (market) this.#updateCartAttributes({ _upsell_market: market });
      }

      // Mirror live theme _trackUpsellImpression (script-cdr.js lines 1787-1800):
      // write shown product handles to cart attribute + fire Shopify analytics event
      // so order-level reports capture upsell exposure per market.
      #trackImpression() {
        // Re-query the track and bail if this element has been morphed out of the DOM since the
        // impression was scheduled — never log an impression for a detached rail.
        this.track = this.querySelector('[data-cart-upsell-track]');
        if (!this.track || !this.track.isConnected) return;
        const handles = Array.from(this.track.querySelectorAll('[data-cart-upsell-card]'))
          .map((c) => c.dataset.handle)
          .filter(Boolean)
          .join(',');
        if (!handles) return;
        // Memoise per handle-set for the page session: the drawer re-renders on every cart:update,
        // so opening/closing it repeatedly otherwise logged the same impression N times.
        if (PW_UPSELL_IMPRESSED.has(handles)) return;
        PW_UPSELL_IMPRESSED.add(handles);
        const market = this.dataset.market || '';
        this.#updateCartAttributes({ _upsell_products_shown: handles });
        this.#fireUpsellEvent('cart_upsell_impression', {
          products: handles.split(','),
          market,
          source: 'cart_drawer',
        });
      }
    }
  );
}
